/// <reference types="i18n" />
import {
  MAX_PASSWORD_LENGTH,
  MAX_SCREENNAME_LENGTH,
  MIN_PASSWORD_LENGTH,
  MIN_SCREENNAME_LENGTH
} from './GalleryConstants'
import {Platform} from './types'
import {
  illegalScreennameCharacterCombos,
  validScreennameCharacters,
  validScreennameStartingCharacter,
  validScreennameTrailingCharacter
} from './validateNameUtils'

export const passwordSymbols = '!"#$%&\'()*+,-./:;<=>?@[]^_`{|}~]'
export const passwordSymbolRegex = /[ !"#$%&'()\*\+,-.\/:;<=>?@\[\\\]^_`{|}~]/
export const passwordAlphabeticRegex = /[A-Za-z]/
export const passwordNumberRegex = /\d/
export const passwordLengthRegex = new RegExp(`^.{${MIN_PASSWORD_LENGTH},${MAX_PASSWORD_LENGTH}}$`)
const usernameLengthRegex = new RegExp(`^.{${MIN_SCREENNAME_LENGTH},${MAX_SCREENNAME_LENGTH}}$`)

const illegalScreennameCharacterCombosRegex = () => {
  // Create a negative lookahead query for each illegal character combo
  const regexList = illegalScreennameCharacterCombos.map((str) => `(?=(^((?!${str}).)*$))`)
  return RegExp(regexList.join(''))
}

export function getPasswordRequirements(localizer: typeof i18n.__) {
  const regexes = [
    {
      regex: passwordLengthRegex.source,
      description: localizer('passwordLengthRequirement', {
        min: `${MIN_PASSWORD_LENGTH}`,
        max: `${MAX_PASSWORD_LENGTH}`
      })
    },
    {
      regex: passwordNumberRegex.source,
      description: localizer('Password must contain a number')
    },
    {
      regex: passwordAlphabeticRegex.source,
      description: localizer('Password must contain a letter')
    },
    {
      regex: passwordSymbolRegex.source,
      description: localizer('Password must contain one of these symbols:', passwordSymbols)
    }
  ]
  return {
    regex: regexes,
    localizedDescription: localizer('passwordRequirementsDescription')
  }
}

export function getUsernameRequirements(localizer: typeof i18n.__) {
  const regexes = [
    {
      regex: usernameLengthRegex.source,
      description: localizer('screennameLengthRequirement', {
        min: `${MIN_SCREENNAME_LENGTH}`,
        max: `${MAX_SCREENNAME_LENGTH}`
      })
    },
    {
      regex: validScreennameCharacters.source,
      description: localizer('screennameCharacterSetRequirement')
    },
    {
      regex: validScreennameStartingCharacter.source,
      description: localizer('screennameAlphaStart')
    },
    {
      regex: validScreennameTrailingCharacter.source,
      description: localizer('screennameAlphanumericEnd')
    },
    {
      regex: illegalScreennameCharacterCombosRegex().source,
      description: localizer('screennameRepeatedCharacterLimitation')
    }
  ]
  return {
    regex: regexes,
    localizedDescription: localizer('screennameCharacterSetRequirement')
  }
}

// For use in both api and collab-api auth flows. Returns undefined if invalid.
export function platformFromString(platformString: string) {
  switch (platformString.toLowerCase()) {
    case 'android':
      return Platform.ANDROID
    case 'ios':
      return Platform.IOS
    // This value can be forwarded from one service to another but should not be in here otherwise
    case 'missing':
      return Platform.MISSING
    case 'web':
      return Platform.WEB
  }

  return undefined
}
